import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthenticatedApi } from "../auth";
import { getDictionariesBulk, getPdssChildren as fetchPdssChildren } from "../api/dictionariesApi";

// Dictionary data rarely changes — cache for 5 minutes so repeated page visits
// and multiple components requesting the same names share a single network call.
const DICTIONARY_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Fetch multiple dictionaries on mount and expose them as a map.
 * Inactive items (active === false) are filtered out by default.
 *
 * Uses react-query with a 5-minute staleTime so the same set of dictionary names
 * is only fetched once per user session (per the staleTime window) — subsequent
 * mounts / components reading the same names hit the cache, no extra HTTP call.
 *
 * @param {string[]} names  Bulk dictionary names, e.g. ["document-types","tags"]
 * @param {{ keepInactive?: boolean, keepInactiveNames?: string[] }} opts
 * @returns {{ dictionaries: Record<string, Array>, loading: boolean, error: any }}
 */
export function useDictionaries(
    names = [],
    { keepInactive = false, keepInactiveNames = [] } = {},
) {
    const { callProtectedApi, apiBaseUrl } = useAuthenticatedApi();

    // Normalise names to a stable ordering so `["a","b"]` and `["b","a"]`
    // share the same cache entry and don't cause duplicate fetches.
    const normalisedNames = useMemo(
        () => [...new Set(names.map(String))].sort(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(names)],
    );

    const { data: raw, isLoading, error } = useQuery({
        queryKey: ["dictionaries", normalisedNames],
        queryFn: () => getDictionariesBulk(apiBaseUrl, callProtectedApi, normalisedNames),
        enabled: normalisedNames.length > 0,
        staleTime: DICTIONARY_STALE_TIME_MS,
        refetchOnWindowFocus: false,
    });

    // Filter inactive items unless the caller opts out. Memoised so the returned
    // reference is stable across renders when the source data hasn't changed.
    const dictionaries = useMemo(() => {
        if (!raw) return {};
        const keepInactiveLookup = new Set(
            [...new Set(keepInactiveNames)].map(String),
        );
        const filtered = {};
        for (const [key, items] of Object.entries(raw)) {
            filtered[key] =
                keepInactive || keepInactiveLookup.has(String(key))
                    ? items
                    : items.filter((i) => i.active !== false);
        }
        return filtered;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raw, keepInactive, JSON.stringify(keepInactiveNames)]);

    return {
        dictionaries,
        // Preserve original contract: loading is only true while the query is in-flight
        // AND at least one dictionary name was requested.
        loading: isLoading && normalisedNames.length > 0,
        error: error ?? null,
    };
}

/**
 * Helper that fetches PDSS child levels for a given parent.
 * Returns a callable function so consumers can invoke it on selection.
 */
export function usePdssChildren() {
    const { callProtectedApi, apiBaseUrl } = useAuthenticatedApi();
    const callRef = useRef(callProtectedApi);

    useEffect(() => {
        callRef.current = callProtectedApi;
    }, [callProtectedApi]);

    const getChildren = useCallback(
        async (parentId) => {
            if (!parentId) return [];
            const items = await fetchPdssChildren(apiBaseUrl, callRef.current, parentId);
            return items.filter((i) => i.active !== false);
        },
        [apiBaseUrl]
    );

    return { getChildren };
}
